/**
 * Gift Form Handler
 * Manages the display and validation of gift form fields
 * Adds cart attributes when product is added to cart
 */
class GiftForm {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.checkbox = document.getElementById(`gift-checkbox-${sectionId}`);
    this.fieldsContainer = document.getElementById(`gift-fields-${sectionId}`);
    this.emailInput = document.getElementById(`gift-email-${sectionId}`);
    this.nameInput = document.getElementById(`gift-name-${sectionId}`);
    this.messageTextarea = document.getElementById(`gift-message-${sectionId}`);
    this.dateInput = document.getElementById(`gift-date-${sectionId}`);
    this.messageCount = document.getElementById(`gift-message-count-${sectionId}`);
    this.emailError = document.getElementById(`gift-email-error-${sectionId}`);
    this.form = this.checkbox?.closest('form');

    if (!this.checkbox || !this.fieldsContainer) {
      return;
    }

    this.init();
  }

  init() {
    // Toggle fields visibility on checkbox change
    this.checkbox.addEventListener('change', () => {
      this.toggleFields();
      this.validateForm();
    });

    // Email validation
    if (this.emailInput) {
      this.emailInput.addEventListener('blur', () => {
        this.validateEmail();
      });

      this.emailInput.addEventListener('input', () => {
        this.clearEmailError();
      });
    }

    // Message character counter
    if (this.messageTextarea && this.messageCount) {
      this.messageTextarea.addEventListener('input', () => {
        this.updateCharCount();
      });
    }

    // Form submission validation and cart attributes
    if (this.form) {
      this.form.addEventListener('submit', async (e) => {
        if (this.checkbox.checked && !this.validateForm()) {
          e.preventDefault();
          return false;
        }
        
        // If checkbox is checked, we'll update attributes after product is added
        if (this.checkbox.checked) {
          // Store form data before submission
          this.pendingGiftData = this.getGiftFormData();
        }
      });

      // Listen for successful cart addition to update cart attributes
      // Multiple event listeners to catch cart updates from different sources
      document.addEventListener('cart:updated', () => {
        if (this.checkbox && this.checkbox.checked) {
          setTimeout(() => this.updateCartAttributes(), 300);
        }
      });

      // Listen to the cart update event from the theme's pub/sub system
      this.subscribeToCartUpdate();

      // Also intercept form submission to add attributes after product is added
      this.interceptFormSubmission();
      
      // Fallback: Check cart periodically if attributes are missing
      this.startAttributeCheck();
    }
  }

  toggleFields() {
    if (this.checkbox.checked) {
      this.fieldsContainer.style.display = 'block';
      // Add required attribute to email if checkbox is checked
      if (this.emailInput) {
        this.emailInput.setAttribute('required', 'required');
      }
    } else {
      this.fieldsContainer.style.display = 'none';
      // Remove required attribute and clear fields if checkbox is unchecked
      if (this.emailInput) {
        this.emailInput.removeAttribute('required');
        this.emailInput.value = '';
        this.clearEmailError();
      }
      // Clear other fields
      const nameInput = document.getElementById(`gift-name-${this.sectionId}`);
      if (nameInput) nameInput.value = '';
      if (this.messageTextarea) {
        this.messageTextarea.value = '';
        this.updateCharCount();
      }
      const dateInput = document.getElementById(`gift-date-${this.sectionId}`);
      if (dateInput) dateInput.value = '';
    }
  }

  validateEmail() {
    if (!this.emailInput || !this.checkbox.checked) {
      return true;
    }

    const email = this.emailInput.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      this.showEmailError('L\'e-mail est requis');
      return false;
    }

    if (!emailRegex.test(email)) {
      this.showEmailError('Veuillez entrer une adresse e-mail valide');
      return false;
    }

    this.clearEmailError();
    return true;
  }

  showEmailError(message) {
    if (this.emailError) {
      this.emailError.textContent = message;
      this.emailError.style.display = 'block';
      this.emailInput?.classList.add('gift-form__input--error');
    }
  }

  clearEmailError() {
    if (this.emailError) {
      this.emailError.textContent = '';
      this.emailError.style.display = 'none';
      this.emailInput?.classList.remove('gift-form__input--error');
    }
  }

  addErrorClass(element) {
    if (element) {
      element.classList.add('gift-form__input--error', 'gift-form__textarea--error');
    }
  }

  removeErrorClass(element) {
    if (element) {
      element.classList.remove('gift-form__input--error', 'gift-form__textarea--error');
    }
  }

  updateCharCount() {
    if (this.messageTextarea && this.messageCount) {
      const count = this.messageTextarea.value.length;
      this.messageCount.textContent = count;
      
      // Add warning class if approaching limit
      if (count > 180) {
        this.messageCount.classList.add('gift-form__char-count--warning');
      } else {
        this.messageCount.classList.remove('gift-form__char-count--warning');
      }
    }
  }

  validateForm() {
    if (!this.checkbox.checked) {
      return true;
    }

    return this.validateEmail();
  }

  subscribeToCartUpdate() {
    // Subscribe to cart update events from the theme
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      this.cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (response) => {
        if (this.checkbox && this.checkbox.checked) {
          // Delay to ensure cart is fully updated
          setTimeout(() => {
            this.updateCartAttributes();
          }, 500);
        }
      });
    }

    // Also listen for custom cart update events
    document.addEventListener('cart:updated', () => {
      if (this.checkbox && this.checkbox.checked) {
        setTimeout(() => {
          this.updateCartAttributes();
        }, 500);
      }
    });

    // Intercept fetch calls to cart_add_url
    this.interceptCartAdd();
  }

  interceptCartAdd() {
    // Intercept fetch calls to detect when product is added
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(...args) {
      const url = args[0];
      
      // Check if this is a cart add request
      if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('cart_add_url'))) {
        return originalFetch.apply(this, args)
          .then(response => {
            // Clone the response so we can read it and still return it
            const clonedResponse = response.clone();
            
            // Check if the request was successful
            if (response.ok) {
              clonedResponse.json().then(data => {
                // If product was successfully added and checkbox is checked
                if (self.checkbox && self.checkbox.checked && !data.status) {
                  // Wait a bit for cart to be fully updated
                  setTimeout(() => {
                    self.updateCartAttributes();
                  }, 800);
                }
              }).catch(() => {
                // If response is not JSON, still try to update attributes
                if (self.checkbox && self.checkbox.checked) {
                  setTimeout(() => {
                    self.updateCartAttributes();
                  }, 800);
                }
              });
            }
            
            return response;
          });
      }
      
      // For all other requests, use original fetch
      return originalFetch.apply(this, args);
    };
  }

  interceptFormSubmission() {
    // Intercept the form submission to add attributes after product is added
    if (this.form) {
      const originalSubmit = this.form.onsubmit;
      this.form.addEventListener('submit', async (e) => {
        // Let the form submit normally first
        // We'll update attributes after the cart is updated
        if (this.checkbox.checked) {
          // Store that we need to update attributes
          this.needsAttributeUpdate = true;
        }
      }, { once: false });
    }
  }

  getGiftFormData() {
    if (!this.checkbox.checked) {
      return null;
    }

    const data = {
      email: this.emailInput?.value.trim() || '',
      name: this.nameInput?.value.trim() || '',
      message: this.messageTextarea?.value.trim() || '',
      date: this.dateInput?.value || ''
    };

    // Only return data if email is filled (required field)
    if (!data.email) {
      return null;
    }

    return data;
  }

  formatGiftNote(data) {
    let note = '🎁 BON CADEAU\n\n';
    note += `E-mail du destinataire: ${data.email}\n`;
    
    if (data.name) {
      note += `Nom du destinataire: ${data.name}\n`;
    }
    
    if (data.message) {
      note += `Message: ${data.message}\n`;
    }
    
    if (data.date) {
      const dateObj = new Date(data.date + 'T00:00:00');
      const formattedDate = dateObj.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      note += `Date d'envoi: ${formattedDate}\n`;
    }

    return note;
  }

  async verifyCartAttributes() {
    // Verify that attributes are in the cart
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        if (cart.attributes && cart.attributes['Bon cadeau']) {
          console.log('✅ Cart attributes verified:', cart.attributes['Bon cadeau']);
          return true;
        } else {
          console.log('⚠️ Cart attributes not found, updating...');
          return false;
        }
      }
    } catch (error) {
      console.error('Error verifying cart attributes:', error);
    }
    return false;
  }

  async updateCartAttributes() {
    const giftData = this.getGiftFormData();
    
    if (!giftData) {
      // Clear attributes if checkbox is unchecked
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              'Bon cadeau': ''
            }
          })
        });
        
        if (!response.ok) {
          console.error('Failed to clear cart attributes:', response.statusText);
        }
      } catch (error) {
        console.error('Error clearing cart attributes:', error);
      }
      return;
    }

    const giftNote = this.formatGiftNote(giftData);

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'Bon cadeau': giftNote
          }
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        
        // Verify the attribute was saved
        if (cartData.attributes && cartData.attributes['Bon cadeau']) {
          console.log('✅ Cart attributes updated successfully');
          console.log('Attribute content:', cartData.attributes['Bon cadeau']);
        } else {
          console.warn('⚠️ Attributes not found in response, verifying...');
          // Verify by fetching cart again
          setTimeout(() => {
            this.verifyCartAttributes();
          }, 500);
        }
        
        // Trigger cart update event if publish function exists
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'gift-form',
            cartData: cartData
          });
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to update cart attributes:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error updating cart attributes:', error);
    }
  }

  startAttributeCheck() {
    // Fallback method: periodically check if attributes need to be updated
    // This ensures attributes are set even if events are missed
    if (this.checkbox && this.checkbox.checked) {
      let checkCount = 0;
      const maxChecks = 5; // Check 5 times over 2.5 seconds
      
      this.attributeCheckInterval = setInterval(() => {
        checkCount++;
        
        // Check if we have pending gift data
        if (this.pendingGiftData) {
          this.updateCartAttributes();
          this.pendingGiftData = null;
        }
        
        // Stop checking after max attempts
        if (checkCount >= maxChecks) {
          clearInterval(this.attributeCheckInterval);
        }
      }, 500);
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.attributeCheckInterval) {
      clearInterval(this.attributeCheckInterval);
    }
  }
}

// Initialize gift forms when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const giftFormWrappers = document.querySelectorAll('.gift-form-wrapper');
  giftFormWrappers.forEach((wrapper) => {
    const sectionId = wrapper.getAttribute('data-section-id');
    if (sectionId) {
      new GiftForm(sectionId);
    }
  });
});

// Re-initialize when product variant changes (for dynamic product forms)
document.addEventListener('variant:change', () => {
  const giftFormWrappers = document.querySelectorAll('.gift-form-wrapper');
  giftFormWrappers.forEach((wrapper) => {
    const sectionId = wrapper.getAttribute('data-section-id');
    if (sectionId) {
      new GiftForm(sectionId);
    }
  });
});

